import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
    XMarkIcon,
    MagnifyingGlassIcon,
    DocumentChartBarIcon,
    CalendarDaysIcon,
    BuildingOfficeIcon,
    WrenchScrewdriverIcon,
    CheckIcon,
} from '@heroicons/react/24/outline'

const ModalGerarRelatorio = ({
    open,
    onClose,
    onSave,
    datasExibicao = [],
    loadingDatas = false,
    onSearchEmpreendimentos,
    onSelectEmpreendimento,
    onSearchObras,
    empreendimentos = [],
    loadingEmpreendimentos = false,
    obras = [],
    loadingObras = false,
}) => {
    const [dataExibicaoId, setDataExibicaoId] = useState('')
    const [empreendimentoSearch, setEmpreendimentoSearch] = useState('')
    const [obraSearch, setObraSearch] = useState('')
    const [obrasSearched, setObrasSearched] = useState(false)
    const [selectedEmpreendimento, setSelectedEmpreendimento] = useState(null)
    const [selectedObra, setSelectedObra] = useState(null)
    const [error, setError] = useState(null)

    const reset = () => {
        setDataExibicaoId('')
        setEmpreendimentoSearch('')
        setObraSearch('')
        setObrasSearched(false)
        setSelectedEmpreendimento(null)
        setSelectedObra(null)
        setError(null)
    }

    const handleClose = () => {
        reset()
        onClose?.()
    }

    useEffect(() => {
        if (!open) reset()
    }, [open])

    const handleDataChange = (id) => {
        setDataExibicaoId(id)
        setEmpreendimentoSearch('')
        setObraSearch('')
        setObrasSearched(false)
        setSelectedEmpreendimento(null)
        setSelectedObra(null)
        setError(null)
        if (id) onSearchEmpreendimentos?.('')
    }

    const handleSearchEmpreendimentos = () => {
        if (!dataExibicaoId) {
            setError('Selecione uma data de exibição primeiro')
            return
        }
        onSearchEmpreendimentos?.(empreendimentoSearch)
    }

    const handleSelectEmpreendimento = (item) => {
        setSelectedEmpreendimento(item)
        setObraSearch('')
        setObrasSearched(false)
        setSelectedObra(null)
        setError(null)
        onSelectEmpreendimento?.(item)
    }

    const handleSearchObras = () => {
        if (!selectedEmpreendimento) {
            setError('Selecione um empreendimento primeiro')
            return
        }
        if (!obraSearch.trim()) {
            setError('Digite um termo para buscar a obra')
            return
        }
        setError(null)
        setObrasSearched(true)
        onSearchObras?.(selectedEmpreendimento.id, obraSearch)
    }

    const handleSubmit = () => {
        if (!dataExibicaoId) { setError('Selecione a data de exibição'); return }
        if (!selectedObra) { setError('Selecione a obra'); return }
        onSave?.({
            data_exibicao_id: dataExibicaoId,
            obra_id: selectedObra.id,
        })
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all">
                                <div className="relative bg-gray-900 px-6 pt-6 pb-14 overflow-hidden">
                                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-brand-yellow/10" />
                                    <div className="relative flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-brand-yellow flex items-center justify-center flex-shrink-0 shadow-lg">
                                                <DocumentChartBarIcon className="w-7 h-7 text-gray-900" />
                                            </div>
                                            <div>
                                                <Dialog.Title as="h3" className="text-xl font-bold text-white">Gerar relatório</Dialog.Title>
                                                <p className="text-sm text-gray-400 mt-0.5">Escolha o período, empreendimento e obra</p>
                                            </div>
                                        </div>
                                        <button type="button" onClick={handleClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <div className="relative -mt-6 mx-6 mb-6 z-10">
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 space-y-5">
                                        {/* Data de exibição */}
                                        <div>
                                            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                                <CalendarDaysIcon className="w-3.5 h-3.5" /> Data de exibição
                                            </label>
                                            {loadingDatas ? (
                                                <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                                                    <span className="h-4 w-4 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin" />
                                                    Carregando datas...
                                                </div>
                                            ) : (
                                                <select
                                                    value={dataExibicaoId}
                                                    onChange={(e) => handleDataChange(e.target.value)}
                                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-yellow/40 focus:border-brand-yellow bg-white text-sm"
                                                >
                                                    <option value="">Selecione o período</option>
                                                    {datasExibicao.map((item) => (
                                                        <option key={item.id} value={item.id}>
                                                            {item.data_inicial} — {item.data_final}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>

                                        {/* Empreendimento */}
                                        <div className={!dataExibicaoId ? 'opacity-50 pointer-events-none' : ''}>
                                            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                                <BuildingOfficeIcon className="w-3.5 h-3.5" /> Empreendimento
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={empreendimentoSearch}
                                                    onChange={(e) => setEmpreendimentoSearch(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchEmpreendimentos()}
                                                    placeholder="Buscar por nome ou código..."
                                                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-yellow/40 focus:border-brand-yellow"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleSearchEmpreendimentos}
                                                    className="px-3 py-2.5 rounded-xl bg-gray-900 text-brand-yellow hover:bg-gray-800 transition-colors"
                                                >
                                                    <MagnifyingGlassIcon className="w-5 h-5" />
                                                </button>
                                            </div>

                                            {loadingEmpreendimentos ? (
                                                <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
                                                    <span className="h-4 w-4 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin" />
                                                    Buscando empreendimentos...
                                                </div>
                                            ) : empreendimentos.length > 0 && (
                                                <div className="mt-2 max-h-36 overflow-y-auto space-y-1.5">
                                                    {empreendimentos.map((item) => (
                                                        <button
                                                            key={item.id}
                                                            type="button"
                                                            onClick={() => handleSelectEmpreendimento(item)}
                                                            className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${selectedEmpreendimento?.id === item.id
                                                                ? 'border-brand-yellow bg-brand-yellow/10'
                                                                : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}
                                                        >
                                                            <div className="flex items-center justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">{item.nome_empreendimento}</p>
                                                                    <p className="text-xs text-gray-400">{item.codigo_empreendimento}</p>
                                                                </div>
                                                                {selectedEmpreendimento?.id === item.id && (
                                                                    <CheckIcon className="w-4 h-4 text-gray-900 flex-shrink-0" />
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Obra */}
                                        <div className={!selectedEmpreendimento ? 'opacity-50 pointer-events-none' : ''}>
                                            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                                <WrenchScrewdriverIcon className="w-3.5 h-3.5" /> Obra
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={obraSearch}
                                                    onChange={(e) => setObraSearch(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchObras()}
                                                    placeholder="Buscar por nome ou código..."
                                                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-yellow/40 focus:border-brand-yellow"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleSearchObras}
                                                    className="px-3 py-2.5 rounded-xl bg-gray-900 text-brand-yellow hover:bg-gray-800 transition-colors"
                                                >
                                                    <MagnifyingGlassIcon className="w-5 h-5" />
                                                </button>
                                            </div>

                                            {loadingObras ? (
                                                <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
                                                    <span className="h-4 w-4 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin" />
                                                    Buscando obras...
                                                </div>
                                            ) : obras.length === 0 ? (
                                                <p className="text-sm text-gray-400 py-2">
                                                    {obrasSearched ? 'Nenhuma obra encontrada.' : 'Busque uma obra por nome ou código.'}
                                                </p>
                                            ) : (
                                                <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5">
                                                    {obras.map((item) => (
                                                        <button
                                                            key={item.id}
                                                            type="button"
                                                            onClick={() => { setSelectedObra(item); setError(null) }}
                                                            className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${selectedObra?.id === item.id
                                                                ? 'border-brand-yellow bg-brand-yellow/10'
                                                                : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}
                                                        >
                                                            <div className="flex items-center justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">{item.nome_obra}</p>
                                                                    <p className="text-xs text-gray-400">{item.codigo_obra}</p>
                                                                </div>
                                                                {selectedObra?.id === item.id && (
                                                                    <CheckIcon className="w-4 h-4 text-gray-900 flex-shrink-0" />
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {error && (
                                            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</p>
                                        )}

                                        <div className="flex gap-3 pt-1">
                                            <button
                                                type="button"
                                                onClick={handleClose}
                                                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSubmit}
                                                disabled={!dataExibicaoId || !selectedObra}
                                                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Gerar relatório
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalGerarRelatorio
